<template>
  <div class="row mb-2">
    <div class="col">
      <div class="form-inline">
        <SelectButton v-model="selectedFilter" :options="filterOptions" id="shipmentFilter" class="mr-2">
          <template #option="slotProps">
            <div :class="[getLabelClass(slotProps.option, 'bg-'), slotProps.option !== 'Pending' ? 'text-white' : 'text-dark']" class="px-3 py-1 tab-content">
              {{ slotProps.option }} ({{ countOfOrdersInFilter(slotProps.option) }})
            </div>
          </template>
        </SelectButton>

        <div class="search-input-wrapper">
          <input v-model="search" class="form-control form-control-lg" placeholder="Search">
          <span v-if="search" @click="clearSearch">x</span>
        </div>
      </div>
    </div>
  </div>

  <DataTable :value="filteredOrders"
             responsiveLayout="stack" breakpoint="992px"
             class="p-datatable-sm"
             :rowClass="() => { return 'table-row' }" stripedRows>
    <Column header="Destination">
      <template #body="slotProps">
        <template v-if="slotProps.data.address.name">{{ slotProps.data.address.company_name }} {{ slotProps.data.address.name }}<br></template>
        {{ slotProps.data.address.address1 }}<br>
        <template v-if="slotProps.data.address.address2">{{ slotProps.data.address.address2 }}<br></template>
        {{ slotProps.data.address.city }}, {{ slotProps.data.address.state }} {{ slotProps.data.address.postcode }}<br>
        <template v-if="slotProps.data.address.phone"><Phone v-model="slotProps.data.address.phone" /></template>
      </template>
    </Column>
    <Column field="id" header="Display #"></Column>
    <Column header="Total">
      <template #body="slotProps">
        {{ formatDollars(slotProps.data.total) }}
      </template>
    </Column>
    <Column header="Status">
      <template #body="slotProps">
        <div>
          Placed: {{ moment(slotProps.data.created_at).format('M/D/YY h:mm A') }}
        </div>
      </template>
    </Column>
    <Column header="Shipments">
      <template #body="slotProps">
        <div v-for="(shipment, index) in slotProps.data.shipments">
          <hr v-if="index !== 0">
          <div v-if="shipment.delivered_at"><strong>Delivered:</strong> {{ moment(shipment.delivered_at).format('dddd MMM D, YYYY') }}</div>
          <div v-else-if="shipment.shipped_at"><strong>Shipped:</strong> {{ moment(shipment.shipped_at).format('dddd MMM D, YYYY') }}</div>
          <strong v-else>Pending</strong>

          <div v-for="item in shipment.items" class="ml-3">
            <strong>{{ item.qty }}</strong> x <span v-if="item.sku" class="text-secondary">({{ item.sku }})</span><strong>&nbsp;{{ item.name }}</strong><br>
          </div>

          <div v-if="shipment.events" class="ml-3">
            <button v-if="shipment.tracking_number" class="btn btn-outline-primary btn-sm" @click="showShippingEvents(shipment.events)">Track Package</button>

            <div v-if="shipment.tracking_number" class="d-block">
              <a :href="`https://www.fedex.com/fedextrack?trknbr=${shipment.tracking_number}`"
                 target="_blank" class="d-block mt-3 mt-lg-0" v-if="shipment.carrier == 'fedex'">
                FedEx {{ shipment.tracking_number }}
              </a>
              <a :href="`http://wwwapps.ups.com/WebTracking/track?track=yes&trackNums=${shipment.tracking_number}`"
                 target="_blank"
                 class="d-inline-block mr-2" v-else-if="shipment.carrier == 'ups'">
                UPS {{ shipment.tracking_number }}
              </a>
            </div>

            <div v-if="shipment.tracking_number" class="d-block">
              <button class="badge badge-secondary cursor-pointer mt-3 mt-lg-0"
                      @click="copyTrackingNumber(shipment.tracking_number)">
                Copy
              </button>
            </div>
          </div>
        </div>
      </template>
    </Column>
    <Column header="Status">
      <template #body="slotProps">
        <span :class="getLabelClass(slotProps.data.status_desc, 'pt-1 badge badge-')">
          {{ slotProps.data.status_desc }}
        </span>
      </template>
    </Column>
    <Column header="">
      <template #body="slotProps">
        <inertia-link class="btn btn-outline-secondary"
                      :href="getViewOrderLink(slotProps.data.id)">
          <i class="fa fa-eye"></i>
        </inertia-link>
      </template>
    </Column>
  </DataTable>

  <ShippingEvents :defaultEvents="events" @close="events = null"></ShippingEvents>
</template>

<script>
import moment from "moment";
import ShippingEvents from '@/Components/Modals/ShippingEvents'
import SelectButton from 'primevue/selectbutton'

export default {
  components: {
    ShippingEvents,
    SelectButton,
  },
  props: {
    orders: Array,
  },
  data() {
    return {
      events: null,
      filteredOrders: this.orders,
      search: '',
      selectedFilter: 'All',
      filterOptions: [
        'All',
        'Pending',
        'Shipped',
        'Delivered',
      ],
    };
  },
  created() {
    this.moment = moment
  },
  watch: {
    selectedFilter() {
      this.filterList();
    },
    search() {
      this.filterList();
    },
  },
  methods: {
    filterList() {
      let filteredList = this.orders;

      if (this.selectedFilter !== 'All') {
        filteredList = filteredList.filter((shipment) => {
          return shipment.status_desc === this.selectedFilter;
        });
      }

      if (this.search.length) {
        let search = this.search.toLowerCase();
        filteredList = filteredList.filter((order) => {
          return (order.address.name || '').toLowerCase().includes(search)
            || (order.address.address1 || '').toLowerCase().includes(search)
            || (order.address.city || '').toLowerCase().includes(search)
            || (order.address.postcode || '').toLowerCase().includes(search)
            || (order.address.state || '').toLowerCase().includes(search)
            || (order.shipments || '').filter((shipment) => {
              if (!shipment.items) return false;
              return shipment.items.filter((item) => {
                return (item.name || '').toLowerCase().includes(search)
                  || (item.sku || '').toLowerCase().includes(search);
              }).length;
            }).length;
        });
      }

      this.filteredOrders = filteredList;
    },
    showShippingEvents(events) {
      this.events = events;
    },
    copyTrackingNumber(number) {
      try {
        navigator.clipboard.writeText(number);
        toastr.success('Tracking number copied to clipboard.');
      } catch (e) {
        toastr.error('Unable to access the clipboard.');
      }
    },
    countOfOrdersInFilter(status) {
      let orders = this.search ? this.filteredOrders : this.orders;

      if (status === 'All') {
        return orders.length;
      }

      return orders.filter((order) => {
        return order.status_desc === status;
      }).length;
    },
    getLabelClass(status, style) {
      switch (status) {
        case 'Shipped':
          return style + 'primary';
        case 'Delivered':
          return style + 'success';
        case 'Pending':
          return style + 'warning';
        default:
          return style + 'info';
      }
    },
    clearSearch() {
      this.search = '';
    },
    getViewOrderLink(orderId) {
      return this.isMtd()
        ? route('mtd.order.view', orderId)
        : route('order.view', orderId);
    }
  }
}
</script>

<style>
.search-input-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  margin-left: auto;
}

.search-input-wrapper span {
  position: absolute;
  display: block;
  right: 5px;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  color: #fff;
  background-color: #ccc;
  text-align: center;
  line-height: 1em;
  cursor: pointer;
}

.search-input-wrapper input {
  padding-right: 18px;
  box-sizing: border-box;
}

#shipmentFilter .p-button {
  padding: 0;
}

#shipmentFilter .p-button.p-highlight {
  border-bottom: 2px solid rgba(0, 0, 0, 0.85);
}

#shipmentFilter .p-button.p-highlight:focus {
  box-shadow: none;
}

#shipmentFilter .p-button.p-highlight .tab-content {
  padding-top: 7.5px !important;
}

.item-subtext {
  border-left: 2px solid #000;
  padding-left: 5px;
  display: inline-block;
  font-style: italic;
}
</style>
